/**
 * DiffViewer — line-oriented, side-by-side or unified diff of two
 * text snapshots.
 *
 * CSK demos that show a "before / after" neutralization narrative
 * reach for a diff surface again and again. The viewer takes two
 * snapshots, computes the longest-common-subsequence line diff, and
 * renders the result with token-aligned add / remove gutters.
 *
 * Single responsibility: pure-text line diff. Multi-character intra-line
 * differences, syntax highlighting, and structural diffs are explicitly
 * out of scope; consumers that need them compose this viewer with
 * {@link CodeBlock} or a richer renderer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { GRAY, SAFE, WARN } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type DiffMode = "unified" | "split";

export interface DiffViewerProps {
  /** Original text (the "before" snapshot). */
  readonly before: string;
  /** Updated text (the "after" snapshot). */
  readonly after: string;
  /** Layout: `unified` is a single column, `split` is side-by-side. Defaults to `unified`. */
  readonly mode?: DiffMode;
  /** Optional label rendered above the `before` column. */
  readonly beforeLabel?: string;
  /** Optional label rendered above the `after` column. */
  readonly afterLabel?: string;
  /** Additional className appended to the wrapper. */
  readonly className?: string;
}

type LineKind = "equal" | "added" | "removed";

interface DiffLine {
  readonly kind: LineKind;
  readonly text: string;
  readonly oldLineNumber?: number;
  readonly newLineNumber?: number;
}

/** Standard LCS line-diff. O(n·m) memory; fine for demo-sized snapshots. */
function diffLines(
  beforeLines: readonly string[],
  afterLines: readonly string[],
): readonly DiffLine[] {
  const m = beforeLines.length;
  const n = afterLines.length;
  // Flat row-major array — keeps indexing under `noUncheckedIndexedAccess`
  // simple and avoids the double-undefined drilldown of `number[][]`.
  const lcs = new Array<number>((m + 1) * (n + 1)).fill(0);
  const lookup = (i: number, j: number): number => lcs[i * (n + 1) + j] ?? 0;
  const store = (i: number, j: number, value: number): void => {
    lcs[i * (n + 1) + j] = value;
  };

  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (beforeLines[i] === afterLines[j]) {
        store(i, j, lookup(i + 1, j + 1) + 1);
      } else {
        store(i, j, Math.max(lookup(i + 1, j), lookup(i, j + 1)));
      }
    }
  }

  const result: DiffLine[] = [];
  let i = 0;
  let j = 0;
  let oldLine = 1;
  let newLine = 1;
  while (i < m && j < n) {
    const beforeText = beforeLines[i] ?? "";
    const afterText = afterLines[j] ?? "";
    if (beforeText === afterText) {
      result.push({
        kind: "equal",
        text: beforeText,
        oldLineNumber: oldLine,
        newLineNumber: newLine,
      });
      i += 1;
      j += 1;
      oldLine += 1;
      newLine += 1;
    } else if (lookup(i + 1, j) >= lookup(i, j + 1)) {
      result.push({ kind: "removed", text: beforeText, oldLineNumber: oldLine });
      i += 1;
      oldLine += 1;
    } else {
      result.push({ kind: "added", text: afterText, newLineNumber: newLine });
      j += 1;
      newLine += 1;
    }
  }
  while (i < m) {
    result.push({ kind: "removed", text: beforeLines[i] ?? "", oldLineNumber: oldLine });
    i += 1;
    oldLine += 1;
  }
  while (j < n) {
    result.push({ kind: "added", text: afterLines[j] ?? "", newLineNumber: newLine });
    j += 1;
    newLine += 1;
  }
  return result;
}

function lineBg(kind: LineKind): string {
  if (kind === "added") return SAFE[100];
  if (kind === "removed") return WARN[100];
  return "transparent";
}

function linePrefix(kind: LineKind): string {
  if (kind === "added") return "+";
  if (kind === "removed") return "−";
  return " ";
}

function lineColor(kind: LineKind): string {
  if (kind === "added") return SAFE[700];
  if (kind === "removed") return WARN[700];
  return GRAY[700];
}

function splitLines(value: string): readonly string[] {
  if (value === "") return [];
  return value.split(/\r?\n/);
}

function UnifiedRow({ line }: { readonly line: DiffLine }): React.JSX.Element {
  return (
    <div
      data-csk-diff-kind={line.kind}
      style={{
        display: "grid",
        gridTemplateColumns: "3rem 3rem 1.25rem 1fr",
        background: lineBg(line.kind),
        color: lineColor(line.kind),
      }}
    >
      <span style={{ color: GRAY[500], paddingLeft: "0.5rem", textAlign: "right" }}>
        {line.oldLineNumber ?? ""}
      </span>
      <span style={{ color: GRAY[500], paddingLeft: "0.5rem", textAlign: "right" }}>
        {line.newLineNumber ?? ""}
      </span>
      <span aria-hidden="true" style={{ paddingLeft: "0.5rem" }}>
        {linePrefix(line.kind)}
      </span>
      <span style={{ paddingRight: "0.5rem", whiteSpace: "pre-wrap" }}>{line.text}</span>
    </div>
  );
}

function SplitRow({ line }: { readonly line: DiffLine }): React.JSX.Element {
  const leftKind: LineKind = line.kind === "added" ? "equal" : line.kind;
  const rightKind: LineKind = line.kind === "removed" ? "equal" : line.kind;
  return (
    <div data-csk-diff-kind={line.kind} style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
      <div style={{ background: lineBg(leftKind), color: lineColor(leftKind) }}>
        <span
          style={{ color: GRAY[500], display: "inline-block", textAlign: "right", width: "2.5rem" }}
        >
          {line.oldLineNumber ?? ""}
        </span>
        <span style={{ paddingRight: "0.5rem", whiteSpace: "pre-wrap" }}>
          {line.kind === "added" ? "" : line.text}
        </span>
      </div>
      <div
        style={{
          background: lineBg(rightKind),
          color: lineColor(rightKind),
          borderLeft: `1px solid ${GRAY[300]}`,
        }}
      >
        <span
          style={{ color: GRAY[500], display: "inline-block", textAlign: "right", width: "2.5rem" }}
        >
          {line.newLineNumber ?? ""}
        </span>
        <span style={{ paddingRight: "0.5rem", whiteSpace: "pre-wrap" }}>
          {line.kind === "removed" ? "" : line.text}
        </span>
      </div>
    </div>
  );
}

/** Renders a line-oriented diff between `before` and `after`. */
export const DiffViewer = React.memo(function DiffViewer({
  before,
  after,
  mode = "unified",
  beforeLabel,
  afterLabel,
  className,
}: DiffViewerProps): React.JSX.Element {
  const lines = useMemo(() => diffLines(splitLines(before), splitLines(after)), [before, after]);

  return (
    <div
      data-csk-diff-viewer
      data-csk-diff-mode={mode}
      className={cn("text-xs", className)}
      style={{
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: "0.5rem",
        color: GRAY[900],
        fontFamily: "ui-monospace, monospace",
        overflowX: "auto",
      }}
    >
      {(beforeLabel !== undefined || afterLabel !== undefined) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: mode === "split" ? "1fr 1fr" : "1fr",
            borderBottom: `1px solid ${GRAY[300]}`,
            background: GRAY[100],
            color: GRAY[700],
            fontSize: "0.6875rem",
            fontWeight: 700,
            padding: "0.4rem 0.75rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          <span>{beforeLabel ?? "Before"}</span>
          {mode === "split" && <span>{afterLabel ?? "After"}</span>}
        </div>
      )}
      <div>
        {lines.map((line, index) =>
          mode === "split" ? (
            <SplitRow key={index} line={line} />
          ) : (
            <UnifiedRow key={index} line={line} />
          ),
        )}
      </div>
    </div>
  );
});
